import { useEffect, useState } from "react";
import { useAuth } from "react-oidc-context";
import styled from "styled-components";
import { FiEdit2, FiCheck, FiX, FiSearch } from "react-icons/fi";
import { fetchCustomers, updateCustomer } from "../utils/api";
import {
  PageHeader,
  PageTitle,
  PageSubtitle,
  Card,
  Input,
  Badge,
  Mono,
  IconButton,
  Button,
  GhostButton,
  Spinner,
  EmptyState,
  Skeleton,
  Row,
} from "../components/ui";
import { formatDateTime } from "../utils/format";
import ErrorNotification from "../components/ErrorNotification";

const Toolbar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.space(3)};
  margin-bottom: ${({ theme }) => theme.space(4)};
  flex-wrap: wrap;
`;

const SearchWrap = styled.div`
  position: relative;
  width: 320px;
  max-width: 100%;
  svg {
    position: absolute;
    left: 12px;
    top: 50%;
    transform: translateY(-50%);
    width: 16px;
    height: 16px;
    color: ${({ theme }) => theme.colors.textMuted};
  }
  input { padding-left: 36px; }
`;

const TableWrap = styled(Card)`
  padding: 0;
  overflow: hidden;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;

  th, td {
    padding: 14px 16px;
    text-align: left;
    vertical-align: middle;
  }

  thead th {
    background: ${({ theme }) => theme.colors.bgAlt};
    color: ${({ theme }) => theme.colors.textMuted};
    font-weight: 600;
    font-size: 12px;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  }

  tbody tr {
    border-bottom: 1px solid ${({ theme }) => theme.colors.border};
    transition: background 0.12s ease;
  }
  tbody tr:last-child { border-bottom: 0; }
  tbody tr:hover { background: ${({ theme }) => theme.colors.surfaceHover}; }

  ${({ theme }) => theme.media.md} {
    display: none;
  }
`;

const Cards = styled.div`
  display: none;
  ${({ theme }) => theme.media.md} {
    display: flex;
    flex-direction: column;
    gap: ${({ theme }) => theme.space(3)};
    padding: ${({ theme }) => theme.space(3)};
  }
`;

const UserRow = styled.div`
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.md};
  padding: ${({ theme }) => theme.space(4)};

  .head {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 12px;
    margin-bottom: 12px;
  }
  .name { font-weight: 600; font-size: 15px; }
  .email { font-size: 13px; color: ${({ theme }) => theme.colors.textMuted}; }

  .grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 10px;
    font-size: 13px;
  }
  .grid .k { color: ${({ theme }) => theme.colors.textMuted}; font-size: 11px; text-transform: uppercase; letter-spacing: 0.04em; }
`;

const FleetWrap = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`;

const Users = () => {
  const auth = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [editingUserId, setEditingUserId] = useState(null);
  const [editedFields, setEditedFields] = useState({});
  const [errors, setErrors] = useState({});
  const [errorMessage, setErrorMessage] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await fetchCustomers(auth.user?.id_token);
        if (!cancelled) setUsers(Array.isArray(data) ? data : []);
      } catch (err) {
        if (!cancelled) setErrorMessage("Failed to load customers: " + err.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [auth.user?.id_token]);

  const filtered = users.filter((u) => {
    const q = search.toLowerCase();
    return (
      (u.name || "").toLowerCase().includes(q) ||
      (u.email || "").toLowerCase().includes(q) ||
      (u.contact_number || "").toLowerCase().includes(q)
    );
  });

  const handleEditClick = (user) => {
    setEditingUserId(user.user_id);
    setEditedFields({
      name: user.name || "",
      contact_number: user.contact_number || "",
    });
    setErrors({});
  };

  const handleCancel = () => {
    setEditingUserId(null);
    setEditedFields({});
    setErrors({});
  };

  const handleSave = async (user_id) => {
    const newErrors = {};
    if (!editedFields.name?.trim()) newErrors.name = true;
    if (!editedFields.contact_number?.trim()) newErrors.contact_number = true;
    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) return;

    try {
      await updateCustomer(auth.user?.id_token, { user_id, ...editedFields });
      setUsers((prev) => prev.map((u) => (u.user_id === user_id ? { ...u, ...editedFields } : u)));
      handleCancel();
    } catch (err) {
      setErrorMessage("Failed to update customer: " + err.message);
    }
  };

  const fleetParts = (fleet) =>
    fleet
      ? Object.values(fleet).map((v, i) => (
          <Mono key={i}>{`${v.country || ""} ${v.emirate || ""} ${v.letter || ""}${v.number || ""} ${v.region || ""}`.trim()}</Mono>
        ))
      : null;

  return (
    <>
      <PageHeader>
        <div>
          <PageTitle>Customers</PageTitle>
          <PageSubtitle>{loading ? "Loading…" : `${users.length} registered customer${users.length === 1 ? "" : "s"}`}</PageSubtitle>
        </div>
      </PageHeader>

      <Toolbar>
        <SearchWrap>
          <FiSearch />
          <Input
            type="search"
            placeholder="Search name, email or phone…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </SearchWrap>
      </Toolbar>

      <TableWrap>
        {loading ? (
          <div style={{ padding: 24 }}>
            <Skeleton h={20} />
            <div style={{ height: 12 }} />
            <Skeleton h={20} />
            <div style={{ height: 12 }} />
            <Skeleton h={20} />
          </div>
        ) : filtered.length === 0 ? (
          <EmptyState>
            <h3>No customers found</h3>
            <p>{search ? "Try a different search." : "Customers will appear here once they sign up."}</p>
          </EmptyState>
        ) : (
          <>
            <Table>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Contact</th>
                  <th>Fleet</th>
                  <th>Joined</th>
                  <th>Profile</th>
                  <th style={{ textAlign: "right" }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((user) => {
                  const isEditing = editingUserId === user.user_id;
                  return (
                    <tr key={user.user_id}>
                      <td>
                        {isEditing ? (
                          <Input
                            value={editedFields.name}
                            onChange={(e) => setEditedFields((p) => ({ ...p, name: e.target.value }))}
                            error={errors.name}
                            aria-label="Name"
                          />
                        ) : (
                          user.name || "—"
                        )}
                      </td>
                      <td style={{ color: "#6B6864" }}>{user.email || "—"}</td>
                      <td>
                        {isEditing ? (
                          <Input
                            value={editedFields.contact_number}
                            onChange={(e) => setEditedFields((p) => ({ ...p, contact_number: e.target.value }))}
                            error={errors.contact_number}
                            aria-label="Contact"
                          />
                        ) : (
                          user.contact_number || "—"
                        )}
                      </td>
                      <td>
                        <FleetWrap>{fleetParts(user.fleet)}</FleetWrap>
                      </td>
                      <td style={{ color: "#6B6864" }}>{formatDateTime(user.created_at)}</td>
                      <td>
                        <Badge tone={user.is_profile_complete ? "success" : "warning"}>
                          {user.is_profile_complete ? "Complete" : "Incomplete"}
                        </Badge>
                      </td>
                      <td>
                        <Row justify="flex-end" gap={1}>
                          {isEditing ? (
                            <>
                              <Button onClick={() => handleSave(user.user_id)} style={{ padding: "6px 12px" }}>
                                <FiCheck /> Save
                              </Button>
                              <GhostButton onClick={handleCancel}>
                                <FiX /> Cancel
                              </GhostButton>
                            </>
                          ) : (
                            <IconButton onClick={() => handleEditClick(user)} aria-label="Edit customer" title="Edit">
                              <FiEdit2 />
                            </IconButton>
                          )}
                        </Row>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>

            <Cards>
              {filtered.map((user) => {
                const isEditing = editingUserId === user.user_id;
                return (
                  <UserRow key={user.user_id}>
                    <div className="head">
                      <div>
                        {isEditing ? (
                          <Input
                            value={editedFields.name}
                            onChange={(e) => setEditedFields((p) => ({ ...p, name: e.target.value }))}
                            error={errors.name}
                          />
                        ) : (
                          <div className="name">{user.name || "—"}</div>
                        )}
                        <div className="email">{user.email || "—"}</div>
                      </div>
                      <Badge tone={user.is_profile_complete ? "success" : "warning"}>
                        {user.is_profile_complete ? "Complete" : "Incomplete"}
                      </Badge>
                    </div>
                    <div className="grid">
                      <div>
                        <div className="k">Contact</div>
                        {isEditing ? (
                          <Input
                            value={editedFields.contact_number}
                            onChange={(e) => setEditedFields((p) => ({ ...p, contact_number: e.target.value }))}
                            error={errors.contact_number}
                          />
                        ) : (
                          user.contact_number || "—"
                        )}
                      </div>
                      <div>
                        <div className="k">Joined</div>
                        {formatDateTime(user.created_at)}
                      </div>
                      <div style={{ gridColumn: "1 / -1" }}>
                        <div className="k" style={{ marginBottom: 6 }}>Fleet</div>
                        <FleetWrap>{fleetParts(user.fleet)}</FleetWrap>
                      </div>
                    </div>
                    <Row justify="flex-end" gap={2} style={{ marginTop: 12 }}>
                      {isEditing ? (
                        <>
                          <Button onClick={() => handleSave(user.user_id)}>
                            <FiCheck /> Save
                          </Button>
                          <GhostButton onClick={handleCancel}>
                            <FiX /> Cancel
                          </GhostButton>
                        </>
                      ) : (
                        <IconButton onClick={() => handleEditClick(user)} aria-label="Edit customer">
                          <FiEdit2 />
                        </IconButton>
                      )}
                    </Row>
                  </UserRow>
                );
              })}
            </Cards>
          </>
        )}
      </TableWrap>

      {loading && (
        <div style={{ display: "flex", justifyContent: "center", marginTop: 20 }}>
          <Spinner />
        </div>
      )}

      {errorMessage && (
        <ErrorNotification
          message={errorMessage}
          duration={4000}
          onClose={() => setErrorMessage(null)}
        />
      )}
    </>
  );
};

export default Users;
